import { useState } from "react";
import { Shield, Plus, Edit2, Trash2, Loader2, BarChart2, Calendar, GitBranch } from "lucide-react";
import { Button } from "../../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";
import { BracketEditorModal } from "./BracketEditorModal";
import { MatchupEditorModal } from "./MatchupEditorModal";
import { PlayoffBracketPreview } from "./playoffs";

export const LeaguePlayoffsSection = ({ league, onAddPlayoff, onEditPlayoff, onDeletePlayoff, onRefresh }) => {
  const playoffs = league.playoffs || [];
  const [seeding, setSeeding] = useState(false);
  const [editingMatchup, setEditingMatchup] = useState(null);
  const [editingBracket, setEditingBracket] = useState(null);

  const handleAutoSeed = async (playoffId) => {
    if (!window.confirm("Auto-seed this bracket based on current standings? This will replace any existing team assignments in the first round.")) return;
    
    setSeeding(true);
    try {
      const res = await axios.post(
        `${API}/leagues/${league.id}/playoffs/${playoffId}/auto-seed`,
        {},
        { headers: getAuthHeader() }
      );
      alert(`${res.data.message}\n\nSeeding:\n${res.data.seeding.map(s => `#${s.seed} ${s.team_name} (${s.record})`).join('\n')}`);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to auto-seed:", err);
      alert("Failed to auto-seed bracket. Make sure teams have standings records.");
    } finally {
      setSeeding(false);
    }
  };

  const handleDuplicateBracket = async (playoff) => {
    const newName = prompt("Enter name for the duplicated bracket:", `${playoff.name} (Copy)`);
    if (!newName) return;
    
    try {
      const bracketData = {
        name: newName,
        team_count: playoff.team_count || playoff.bracket_size,
        bracket_type: playoff.bracket_type,
        format: playoff.format,
        is_active: true,
        conference_a_name: playoff.conference_a_name || "",
        conference_b_name: playoff.conference_b_name || "",
        conference_a_id: playoff.conference_a_id || null,
        conference_b_id: playoff.conference_b_id || null,
        custom_rounds: playoff.custom_rounds || null
      };
      
      await axios.post(
        `${API}/leagues/${league.id}/playoffs`,
        bracketData,
        { headers: getAuthHeader() }
      );
      
      alert(`Bracket "${newName}" created successfully!`);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to duplicate bracket:", err);
      alert("Failed to duplicate bracket. Please try again.");
    }
  };

  const handleQuickAssign = async (playoffId, matchupId, slot, teamId) => {
    const playoff = playoffs.find(p => p.id === playoffId);
    if (!playoff) return;
    
    try {
      const matchup = playoff.matchups.find(m => m.id === matchupId);
      if (!matchup) return;
      
      const updatedMatchup = { ...matchup };
      if (slot === 'team1') {
        updatedMatchup.team1_id = teamId;
      } else {
        updatedMatchup.team2_id = teamId;
      }
      
      const updatedMatchups = playoff.matchups.map(m => 
        m.id === matchupId ? updatedMatchup : m
      );
      
      await axios.put(
        `${API}/leagues/${league.id}/playoffs/${playoff.id}/matchups`,
        { matchups: updatedMatchups },
        { headers: getAuthHeader() }
      );
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to assign team:", err);
      alert("Failed to assign team. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-zinc-400 uppercase">Playoff Brackets</h4>
        <Button onClick={onAddPlayoff} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Create Bracket
        </Button>
      </div>

      {playoffs.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No playoff brackets created</p>
          <p className="text-sm">Create a bracket to set up playoffs</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {playoffs.map(playoff => (
            <div
              key={playoff.id}
              className="bg-zinc-800/50 rounded-lg border border-zinc-700 p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-white font-medium">{playoff.name}</h4>
                  <p className="text-sm text-zinc-500">
                    {playoff.bracket_type === "double_sided" 
                      ? `${(playoff.team_count || playoff.bracket_size) * 2} teams (${playoff.team_count || playoff.bracket_size} per conference)` 
                      : `${playoff.team_count || playoff.bracket_size} teams`} • {(playoff.format || "single_elimination").replace("_", " ")}
                    {playoff.bracket_type === "double_sided" && (
                      <span className="ml-2 text-orange-400">• Conference Championship</span>
                    )}
                  </p>
                  {playoff.bracket_type === "double_sided" && (
                    <p className="text-xs text-zinc-600 mt-1">
                      {playoff.conference_a_name || "Conference A"} vs {playoff.conference_b_name || "Conference B"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingBracket(playoff)}
                    className="px-2 py-1.5 text-xs font-medium text-orange-400 hover:text-white hover:bg-orange-500/20 rounded border border-orange-500/30 flex items-center gap-1"
                    title="Edit all bracket games"
                  >
                    <Calendar className="w-3 h-3" />
                    Bracket Editor
                  </button>
                  <button
                    onClick={() => handleAutoSeed(playoff.id)}
                    disabled={seeding}
                    className="px-2 py-1.5 text-xs font-medium text-green-400 hover:text-white hover:bg-green-500/20 rounded border border-green-500/30 flex items-center gap-1"
                    title="Auto-seed based on standings"
                  >
                    {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <BarChart2 className="w-3 h-3" />}
                    Auto Seed
                  </button>
                  <button
                    onClick={() => handleDuplicateBracket(playoff)}
                    className="px-2 py-1.5 text-xs font-medium text-blue-400 hover:text-white hover:bg-blue-500/20 rounded border border-blue-500/30 flex items-center gap-1"
                    title="Duplicate this bracket"
                  >
                    <GitBranch className="w-3 h-3" />
                    Duplicate
                  </button>
                  <button
                    onClick={() => onEditPlayoff(playoff)}
                    className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDeletePlayoff(playoff.id)}
                    className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/20 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <PlayoffBracketPreview 
                playoff={playoff} 
                teams={league.teams || []} 
                onEditMatchup={(matchup) => setEditingMatchup({ playoff, matchup })}
                onQuickAssign={(matchupId, slot, teamId) => handleQuickAssign(playoff.id, matchupId, slot, teamId)}
              />
            </div>
          ))}
        </div>
      )}

      {editingMatchup && (
        <MatchupEditorModal
          matchup={editingMatchup.matchup}
          playoff={editingMatchup.playoff}
          teams={league.teams || []}
          leagueId={league.id}
          onSave={async (updatedMatchup) => {
            try {
              const updatedMatchups = editingMatchup.playoff.matchups.map(m => 
                m.id === updatedMatchup.id ? updatedMatchup : m
              );
              await axios.put(
                `${API}/leagues/${league.id}/playoffs/${editingMatchup.playoff.id}/matchups`,
                { matchups: updatedMatchups },
                { headers: getAuthHeader() }
              );
              setEditingMatchup(null);
              if (onRefresh) {
                try {
                  onRefresh();
                } catch (refreshErr) {
                  console.warn("Refresh after save:", refreshErr);
                }
              }
            } catch (err) {
              console.error("Failed to update matchup:", err);
              alert("Failed to save matchup. Please try again.");
            }
          }}
          onClose={() => setEditingMatchup(null)}
        />
      )}

      {editingBracket && (
        <BracketEditorModal
          playoff={editingBracket}
          teams={league.teams || []}
          leagueId={league.id}
          onSave={() => {
            setEditingBracket(null);
            if (onRefresh) onRefresh();
          }}
          onClose={() => setEditingBracket(null)}
        />
      )}
    </div>
  );
};
